import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';

/** Palabra individual del segmento de transcripción con sus tiempos (segundos). */
interface TranscriptWord {
  text: string;
  start: number;
  end: number;
  /** Índice global de la palabra en todo el episodio (para el resaltado karaoke). */
  globalIndex: number;
}

/** Un párrafo/segmento de la transcripción, tal como lo entrega el backend. */
interface TranscriptSegmentVM {
  text: string;
  start: number;
  end: number;
  words: TranscriptWord[];
}

@Component({
  selector: 'app-custom-podcast-audio-player',
  templateUrl: './custom-podcast-audio-player.component.html',
  styleUrls: ['./custom-podcast-audio-player.component.scss'],
})
export class CustomPodcastAudioPlayerComponent implements OnInit, OnChanges, OnDestroy {
  // ── Inputs ─────────────────────────────────────────────────────────────────
  @Input() audioUrl: string = '';
  @Input() savedCurrentTime: number = 0;
  @Input() moduleLabel: string = 'titulo de tema';
  @Input() moduleIndex: number = 1; // numero de pisodio dentro del podcast
  @Input() totalModules: number = 3; // total de episodios dentro del podcast
  @Input() audioTitle: string = 'titulo de episodio';
  /** Reproduce automáticamente al cambiar de episodio (no en la primera carga). */
  @Input() autoplay: boolean = false;
  /**
   * Cuando el tema tiene `review_all_duration_media` en true y el episodio aún
   * no está revisado, el usuario debe escuchar el episodio completo: se bloquea
   * el adelanto (seek hacia delante y botón +15s). Retroceder sí se permite.
   */
  @Input() reviewAllIsRequired: boolean = false;
  /**
   * Segmentos de transcripción del episodio (llave `audio.transcript_segments`
   * del JSON del podcast). Cada segmento trae el `text` del párrafo y `words`
   * con los tiempos de inicio/fin de cada palabra, que alimentan el resaltado
   * karaoke y el auto-scroll del modo transcripción.
   */
  @Input() transcriptSegments: any[] = [];

  // ── Outputs ────────────────────────────────────────────────────────────────
  @Output() audioEnded = new EventEmitter<{ currentTime: number }>();
  @Output() audioPaused = new EventEmitter<number>();

  @ViewChild('audioElement', { static: false })
  audioElement!: ElementRef<HTMLAudioElement>;

  /** Contenedor scrolleable del texto de la transcripción. */
  @ViewChild('transcriptScroll') transcriptScrollRef?: ElementRef<HTMLElement>;
  /** Cada párrafo renderizado, para poder centrar el activo al hacer scroll. */
  @ViewChildren('segmentEl') segmentEls?: QueryList<ElementRef<HTMLElement>>;

  constructor(private ngZone: NgZone) {}

  // ── State ──────────────────────────────────────────────────────────────────
  isPlaying = false;
  /**
   * El podcast NO se puede silenciar por requerimiento de producto: el usuario
   * debe escuchar el contenido. Por eso isMuted siempre es false y el volumen
   * tiene un piso mínimo (MIN_VOLUME). Se mantiene la propiedad para no romper
   * el binding del template, pero nunca pasa a true.
   */
  isMuted = false;
  currentTime = 0;
  duration = 0;
  progress = 0; // 0–100

  playbackSpeed = 1;
  showSpeedMenu = false;
  speedOptions: number[] = [0.5, 0.75, 1, 1.25, 1.5, 2];
  showTranscript = false;
  volume = 1; // 0–1

  // ── Transcripción (karaoke) ─────────────────────────────────────────────────
  /** Vista construida a partir de `transcriptSegments` (párrafos + palabras). */
  transcriptVM: TranscriptSegmentVM[] = [];
  /** Todas las palabras en orden, para calcular cuántas ya se pronunciaron. */
  private flatWords: TranscriptWord[] = [];
  /** Nº de palabras ya reveladas (start <= currentTime). Dirige el resaltado. */
  revealedWordCount = 0;
  /** Índice del párrafo que se está pronunciando; se mantiene centrado. */
  activeSegmentIndex = -1;
  /** id del requestAnimationFrame que sincroniza el karaoke mientras reproduce. */
  private transcriptRafId: number | null = null;

  /** Volumen mínimo permitido: nunca se puede llegar a silencio total. */
  readonly MIN_VOLUME = 0.1;

  /** Margen para que un ajuste mínimo del slider no cuente como "adelanto". */
  private readonly SEEK_TOLERANCE = 0.05;

  private hasSetInitialTime = false;
  private shouldAutoplay = false;
  /**
   * true mientras se cambia de episodio (audioUrl) hasta que el nuevo audio
   * tiene metadata. Sirve para ignorar los eventos residuales que el navegador
   * dispara sobre el audio saliente (timeupdate/pause al hacer load()), que si
   * no llegan a mutar el estado y dejan el episodio nuevo como "ya reproducido".
   */
  private isTransitioning = false;
  /** true cuando el audio llegó a su fin natural (no es una pausa del usuario). */
  private isEnded = false;

  // ── Waveform bars ──────────────────────────────────────────────────────────
  // 32 decorative bars — heights expressed as % of max bar height (0–100)
  readonly waveformBars: number[] = [
    28, 46, 70, 90, 58, 36, 78, 62, 44, 72, 84, 52, 34, 66, 88, 56,
    36, 76, 60, 42, 70, 82, 50, 32, 66, 48, 78, 58, 86, 42, 62, 26,
  ];

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  ngOnInit(): void {
    // Cada episodio monta una instancia nueva del reproductor (el contenedor lo
    // recrea vía trackBy por episodio). Por eso el autoplay se decide aquí según
    // el input: al auto-avanzar llega autoplay=true; en la carga inicial del
    // media llega false. Ya no dependemos de "no es firstChange".
    this.shouldAutoplay = this.autoplay;
    this.buildTranscript();
    console.log('[podcast-player] ngOnInit →', { audioUrl: this.audioUrl, autoplay: this.autoplay, savedCurrentTime: this.savedCurrentTime });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['transcriptSegments'] && !changes['transcriptSegments'].firstChange) {
      // Nuevo episodio (o llegada tardía de la data): reconstruir la transcripción
      // y reiniciar el estado de resaltado/scroll.
      this.buildTranscript();
      this.resetTranscriptProgress();
    }
    if (changes['audioUrl']) {
      console.log('[podcast-player] ngOnChanges audioUrl:', {
        firstChange: changes['audioUrl'].firstChange,
        prev: changes['audioUrl'].previousValue,
        curr: changes['audioUrl'].currentValue,
        autoplay: this.autoplay,
        savedCurrentTime: this.savedCurrentTime,
      });
    }
    if (changes['audioUrl'] && !changes['audioUrl'].firstChange) {
      // Marca la transición ANTES de resetear: así los eventos residuales del
      // audio saliente (timeupdate/pause disparados por load()) se ignoran y no
      // dejan la barra del nuevo episodio como si ya se hubiera reproducido.
      this.isTransitioning = true;
      this.resetPlayer();
      // Al avanzar de episodio (cambio de audioUrl) reproducimos automáticamente
      // si así lo pide el contenedor; la primera carga nunca auto-reproduce.
      this.shouldAutoplay = this.autoplay;
    }
  }

  ngOnDestroy(): void {
    // Al cambiar de episodio esta instancia se destruye: detener el audio para
    // que no siga sonando el track saliente mientras monta el nuevo.
    this.stopTranscriptSync();
    const audio = this.audioElement?.nativeElement;
    if (audio) {
      audio.pause();
      audio.src = '';
    }
  }

  // ── Audio element events ───────────────────────────────────────────────────
  onLoadedMetadata(): void {
    const audio = this.audioElement?.nativeElement;
    if (!audio) return;
    // Ignora metadata que no corresponda al episodio actual: durante el cambio de
    // src el audio saliente puede emitir un loadedmetadata residual. Solo el del
    // audioUrl vigente debe manejar restauración/autoplay.
    if (this.isTransitioning && this.audioUrl && audio.currentSrc && audio.currentSrc !== this.audioUrl) {
      console.log('[podcast-player] loadedmetadata IGNORADO (src residual):', audio.currentSrc, '≠', this.audioUrl);
      return;
    }
    // El nuevo episodio ya tiene su metadata: la transición terminó, a partir de
    // aquí los eventos del audio corresponden al episodio actual.
    this.isTransitioning = false;
    this.isEnded = false;
    this.duration = audio.duration;
    console.log('[podcast-player] loadedmetadata OK →', {
      audioUrl: this.audioUrl,
      currentSrc: audio.currentSrc,
      duration: this.duration,
      currentTimeElem: audio.currentTime,
      savedCurrentTime: this.savedCurrentTime,
      shouldAutoplay: this.shouldAutoplay,
      hasSetInitialTime: this.hasSetInitialTime,
    });
    // Solo restauramos el avance guardado si NO venimos de un auto-avance: al
    // encadenar episodios el nuevo siempre arranca desde 0. Restaurar aplica
    // únicamente a la selección/carga manual de un episodio a medio escuchar.
    // No restaurar a una posición prácticamente al final (>= duración - 1s): ese
    // avance equivale a "terminado", así que el episodio debe arrancar en 0.
    const savedIsNearEnd = this.duration > 0 && this.savedCurrentTime >= this.duration - 1;
    if (!this.hasSetInitialTime && !this.shouldAutoplay && this.savedCurrentTime > 0 && !savedIsNearEnd && audio.readyState >= 2) {
      console.log('[podcast-player] RESTAURANDO avance a', this.savedCurrentTime, 's');
      audio.currentTime = this.savedCurrentTime;
      this.currentTime = this.savedCurrentTime;
      this.progress = this.duration > 0 ? (this.savedCurrentTime / this.duration) * 100 : 0;
      this.hasSetInitialTime = true;
    }
    // Garantiza que el audio nunca arranque silenciado.
    audio.muted = false;
    audio.volume = Math.max(this.MIN_VOLUME, this.volume);
    if (this.shouldAutoplay) {
      this.shouldAutoplay = false;
      audio
        .play()
        .then(() => {
          this.isPlaying = true;
          if (this.showTranscript) this.startTranscriptSync();
          console.log('[podcast-player] autoplay OK, reproduciendo desde', audio.currentTime, 's');
        })
        .catch((e) => console.log('[podcast-player] autoplay BLOQUEADO:', e?.name || e));
    }
  }

  onTimeUpdate(): void {
    // Ignora los timeupdate residuales del audio saliente durante un cambio de
    // episodio (evita pisar el progreso ya reseteado del episodio nuevo).
    if (this.isTransitioning) return;
    const audio = this.audioElement?.nativeElement;
    if (!audio) return;
    this.currentTime = audio.currentTime;
    this.progress = this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
    // El evento `timeupdate` ya corre dentro de la zona de Angular, así que
    // actualizar aquí el estado de la transcripción garantiza el resaltado
    // aunque el bucle rAF (mayor frecuencia) no estuviera activo.
    if (this.showTranscript) this.updateTranscriptState(this.currentTime);
  }

  onAudioEnded(): void {
    const audio = this.audioElement?.nativeElement;
    console.log('[podcast-player] onAudioEnded →', {
      currentSrc: audio?.currentSrc,
      currentTime: this.currentTime,
      isTransitioning: this.isTransitioning,
    });
    this.isPlaying = false;
    this.isEnded = true;
    this.stopTranscriptSync();
    this.audioEnded.emit({ currentTime: this.currentTime });
  }

  onAudioPaused(): void {
    // No emitir en fin natural ni durante la transición de episodio: no es una
    // pausa del usuario y persistir last_media_duration aquí corrompería el
    // avance del episodio siguiente.
    if (this.isEnded || this.isTransitioning) return;
    // Only emit if pause was triggered externally (not by our togglePlayPause)
    if (!this.isPlaying) {
      this.audioPaused.emit(this.currentTime);
    }
  }

  // ── Controls ───────────────────────────────────────────────────────────────
  togglePlayPause(): void {
    const audio = this.audioElement?.nativeElement;
    if (!audio) {
      // No real audio — simulate state for UI-only usage
      this.isPlaying = !this.isPlaying;
      return;
    }
    if (this.isPlaying) {
      // No emitir aquí: audio.pause() dispara el evento DOM `pause` →
      // onAudioPaused(), que ya emite una sola vez. Emitir también aquí
      // provocaba una doble petición al pausar.
      audio.pause();
      this.stopTranscriptSync();
    } else {
      // Reproducir de nuevo (incluye reanudar tras el fin) limpia el flag de fin.
      this.isEnded = false;
      audio.play().catch(() => {});
      if (this.showTranscript) this.startTranscriptSync();
    }
    this.isPlaying = !this.isPlaying;
  }

  /**
   * El silencio total está deshabilitado a propósito. Mantener el episodio
   * audible es un requerimiento de producto, por eso esta acción no muteа: a lo
   * sumo lleva el volumen al piso minimo permitido.
   */
  toggleMute(): void {
    this.isMuted = false;
    this.setVolume(this.MIN_VOLUME);
  }

  onVolumeChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.setVolume(parseFloat(input.value));
  }

  private setVolume(value: number): void {
    // Nunca por debajo del piso mínimo: no se permite silencio total.
    this.volume = Math.max(this.MIN_VOLUME, isNaN(value) ? this.MIN_VOLUME : value);
    this.isMuted = false;
    const audio = this.audioElement?.nativeElement;
    if (audio) {
      audio.volume = this.volume;
      audio.muted = false;
    }
  }

  get volumeIcon(): string {
    if (this.volume <= 0.33) return 'volume-low';
    if (this.volume <= 0.66) return 'volume-medium';
    return 'volume-high';
  }

  skipBack(): void {
    const audio = this.audioElement?.nativeElement;
    const newTime = Math.max(0, this.currentTime - 15);
    if (audio) audio.currentTime = newTime;
    this.currentTime = newTime;
    this.progress = this.duration > 0 ? (newTime / this.duration) * 100 : 0;
    if (this.showTranscript) this.syncTranscriptToTime(newTime, true);
  }

  skipForward(): void {
    // Con revisión obligatoria del episodio no se permite adelantar.
    if (this.reviewAllIsRequired) return;
    const audio = this.audioElement?.nativeElement;
    const newTime = Math.min(this.duration || 0, this.currentTime + 15);
    if (audio) audio.currentTime = newTime;
    this.currentTime = newTime;
    this.progress = this.duration > 0 ? (newTime / this.duration) * 100 : 0;
    if (this.showTranscript) this.syncTranscriptToTime(newTime, true);
  }

  toggleSpeedMenu(): void {
    this.showSpeedMenu = !this.showSpeedMenu;
  }

  setPlaybackSpeed(speed: number): void {
    this.playbackSpeed = speed;
    this.showSpeedMenu = false;
    const audio = this.audioElement?.nativeElement;
    if (audio) audio.playbackRate = speed;
  }

  toggleTranscript(): void {
    this.showTranscript = !this.showTranscript;
    if (this.showTranscript) {
      // Al entrar al modo transcripción: posicionar el resaltado en el punto
      // actual del audio y, si está reproduciendo, arrancar la sincronización.
      const t = this.audioElement?.nativeElement?.currentTime ?? this.currentTime;
      this.syncTranscriptToTime(t, true);
      if (this.isPlaying) this.startTranscriptSync();
    } else {
      this.stopTranscriptSync();
    }
  }

  /** Hay transcripción disponible para este episodio. */
  get hasTranscript(): boolean {
    return this.transcriptVM.length > 0;
  }

  /** Una palabra ya fue pronunciada (se muestra nítida). */
  isWordSpoken(word: TranscriptWord): boolean {
    return word.globalIndex < this.revealedWordCount;
  }

  /** Palabra que se está pronunciando justo ahora (última revelada). */
  isWordActive(word: TranscriptWord): boolean {
    return word.globalIndex === this.revealedWordCount - 1;
  }

  // ── Transcripción: construcción y sincronización ────────────────────────────
  /** Aplana `transcriptSegments` a la vista `transcriptVM` + índice de palabras. */
  private buildTranscript(): void {
    this.transcriptVM = [];
    this.flatWords = [];
    let gi = 0;
    for (const seg of this.transcriptSegments || []) {
      if (!seg) continue;
      const rawWords: any[] = Array.isArray(seg.words) ? seg.words : [];
      let words: TranscriptWord[];
      if (rawWords.length) {
        words = rawWords.map((w) => ({
          text: w.word,
          start: Number(w.start) || 0,
          end: Number(w.end) || 0,
          globalIndex: gi++,
        }));
      } else {
        // Segmento sin desglose de palabras: se trata el párrafo como una unidad.
        words = [{ text: seg.text, start: Number(seg.start) || 0, end: Number(seg.end) || 0, globalIndex: gi++ }];
      }
      this.flatWords.push(...words);
      this.transcriptVM.push({
        text: seg.text,
        start: Number(seg.start) || 0,
        end: Number(seg.end) || 0,
        words,
      });
    }
  }

  private resetTranscriptProgress(): void {
    this.revealedWordCount = 0;
    this.activeSegmentIndex = -1;
  }

  /**
   * Arranca el bucle rAF que sincroniza el resaltado con el audio a ~60fps.
   * Corre fuera de la zona de Angular y solo re-entra a la zona (mutando estado
   * → dispara CD) cuando cambia la palabra o el párrafo activo, evitando
   * detección de cambios en cada frame.
   */
  private startTranscriptSync(): void {
    if (this.transcriptRafId != null || !this.hasTranscript) return;
    this.ngZone.runOutsideAngular(() => {
      const tick = () => {
        const audio = this.audioElement?.nativeElement;
        if (audio) {
          const t = audio.currentTime;
          const count = this.countRevealedWords(t);
          const seg = this.findActiveSegment(t);
          if (count !== this.revealedWordCount || seg !== this.activeSegmentIndex) {
            // Mutar el estado dentro de la zona deja que Angular corra su propia
            // detección de cambios (más fiable que un detectChanges manual).
            this.ngZone.run(() => this.updateTranscriptState(t));
          }
        }
        this.transcriptRafId = requestAnimationFrame(tick);
      };
      this.transcriptRafId = requestAnimationFrame(tick);
    });
  }

  private stopTranscriptSync(): void {
    if (this.transcriptRafId != null) {
      cancelAnimationFrame(this.transcriptRafId);
      this.transcriptRafId = null;
    }
  }

  /**
   * Aplica un tiempo concreto a la transcripción (usado por seek/skip y al abrir
   * el panel). `scroll` fuerza recentrar el párrafo activo aunque no cambie.
   */
  private syncTranscriptToTime(time: number, scroll: boolean): void {
    this.updateTranscriptState(time, scroll);
  }

  /**
   * Actualiza palabras reveladas + párrafo activo. Debe llamarse dentro de la
   * zona de Angular (desde `timeupdate`, seek, o envuelto en `ngZone.run`), de
   * modo que la detección de cambios normal refresque el resaltado.
   */
  private updateTranscriptState(time: number, forceScroll = false): void {
    if (!this.hasTranscript) return;
    const seg = this.findActiveSegment(time);
    const segChanged = seg !== this.activeSegmentIndex;
    this.revealedWordCount = this.countRevealedWords(time);
    this.activeSegmentIndex = seg;
    if (segChanged || forceScroll) {
      // Esperar al render antes de medir posiciones para centrar el párrafo.
      requestAnimationFrame(() => this.scrollActiveSegmentIntoView());
    }
  }

  private countRevealedWords(time: number): number {
    let c = 0;
    for (const w of this.flatWords) if (w.start <= time) c++;
    return c;
  }

  private findActiveSegment(time: number): number {
    let idx = -1;
    for (let i = 0; i < this.transcriptVM.length; i++) {
      if (this.transcriptVM[i].start <= time) idx = i;
      else break;
    }
    return idx;
  }

  /** Centra verticalmente el párrafo activo dentro del contenedor scrolleable. */
  private scrollActiveSegmentIntoView(): void {
    const container = this.transcriptScrollRef?.nativeElement;
    if (!container || this.activeSegmentIndex < 0) return;
    const el = this.segmentEls?.get(this.activeSegmentIndex)?.nativeElement;
    if (!el) return;
    const target = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }

  onSeek(event: Event): void {
    const input = event.target as HTMLInputElement;
    const time = parseFloat(input.value);
    const audio = this.audioElement?.nativeElement;
    // Bajo revisión obligatoria solo se permite retroceder: si el usuario intenta
    // adelantar, se restaura el slider al tiempo real de reproducción.
    if (this.reviewAllIsRequired && time > this.currentTime + this.SEEK_TOLERANCE) {
      input.value = String(this.currentTime);
      this.progress = this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
      return;
    }
    if (audio) audio.currentTime = time;
    this.currentTime = time;
    this.progress = this.duration > 0 ? (time / this.duration) * 100 : 0;
    if (this.showTranscript) this.syncTranscriptToTime(time, true);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  formatTime(seconds: number): string {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  getTimeRemaining(): string {
    return this.formatTime(Math.max(0, this.duration - this.currentTime));
  }

  getModuleCounter(): string {
    const idx = this.moduleIndex.toString().padStart(2, '0');
    const total = this.totalModules.toString().padStart(2, '0');
    return `${idx}/${total}`;
  }

  isBarPlayed(index: number): boolean {
    const ratio = (index / (this.waveformBars.length - 1)) * 100;
    return ratio <= this.progress;
  }

  private resetPlayer(): void {
    this.isPlaying = false;
    this.isEnded = false;
    this.hasSetInitialTime = false;
    this.currentTime = 0;
    this.progress = 0;
    this.duration = 0;
    this.stopTranscriptSync();
    this.resetTranscriptProgress();
    const audio = this.audioElement?.nativeElement;
    if (audio) {
      // Solo detenemos la reproducción saliente. NO tocamos currentTime ni
      // llamamos load() sobre el src viejo: el cambio de [src] recarga el
      // elemento por sí solo. Hacer load() aquí (con el src anterior aún en el
      // DOM) dispara un loadedmetadata extra del audio saliente que consumía
      // shouldAutoplay y provocaba que el episodio nuevo se posicionara al final.
      audio.pause();
    }
    console.log('[podcast-player] resetPlayer → estado limpio (autoplay pendiente:', this.autoplay, ')');
  }
}
